import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { Alert, Button, Card, Spinner } from '@easydent/shared-ui';
import { getTenant, suspendTenant, activateTenant, purgeTenant, setTenantDiscount, type TenantDetail } from '@/lib/api';

const field = 'w-full rounded-lg border border-line-strong px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none';

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-center justify-between border-b border-line-faint py-2 text-sm last:border-0">
    <span className="text-muted">{label}</span>
    <span className="font-medium text-body">{children}</span>
  </div>
);

const YesNo: React.FC<{ v: boolean }> = ({ v }) => (
  <span className={v ? 'text-brand-ink' : 'text-subtle-ink'}>{v ? 'Yes' : 'No'}</span>
);

const TenantDetailPage: React.FC = () => {
  const { t: tr } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [t, setT] = useState<TenantDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The agreed price. Percent and amount are mutually exclusive — the API refuses both.
  const [percent, setPercent] = useState('');
  const [amount, setAmount] = useState('');
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  const [savingDiscount, setSavingDiscount] = useState(false);
  const [discountError, setDiscountError] = useState<string | null>(null);

  const load = () => {
    if (!id) return;
    getTenant(id)
      .then((d) => {
        setT(d);
        setPercent(d.discountPercent > 0 ? String(d.discountPercent) : '');
        setAmount(d.discountAmount > 0 ? String(d.discountAmount) : '');
        setUntil(d.discountUntil ?? '');
        setReason(d.discountReason ?? '');
      })
      .catch(() => setError(tr('tenant.loadError', "Could not load the tenant.")));
  };
  useEffect(load, [id]);

  /**
   * Irreversible, so the name is typed rather than clicked past. The API checks it again — this
   * prompt is a speed bump for the operator, not the security boundary.
   */
  const purge = async () => {
    if (!t) return;
    // eslint-disable-next-line no-alert
    const typed = window.prompt(
      tr('tenant.purgeConfirm',
        'This permanently deletes every record for {{name}}. It cannot be undone. Type the clinic name to confirm.',
        { name: t.name }));
    if (typed === null) return;
    setBusy(true);
    setError(null);
    try {
      await purgeTenant(t.id, typed);
      await load();
    } catch (err) {
      const data = (err as { response?: { data?: { detail?: string; title?: string } } })?.response?.data;
      setError(data?.detail ?? data?.title ?? tr('tenant.purgeError', 'Could not delete this clinic.'));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async () => {
    if (!t) return;
    setBusy(true);
    setError(null);
    try {
      await (t.status === 'Suspended' ? activateTenant(t.id) : suspendTenant(t.id));
      load();
    } catch {
      setError(tr('tenant.statusError', "Could not change the tenant status."));
    } finally {
      setBusy(false);
    }
  };

  const saveDiscount = async () => {
    if (!t) return;
    setSavingDiscount(true);
    setDiscountError(null);
    try {
      await setTenantDiscount(t.id, {
        percent: Number(percent) || 0,
        amount: Number(amount) || 0,
        until: until || null,
        reason: reason.trim() || null,
      });
      load();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string; title?: string } } })?.response?.data;
      setDiscountError(detail?.detail ?? detail?.title ?? tr('tenant.discountSaveError', "Could not save the discount."));
    } finally {
      setSavingDiscount(false);
    }
  };

  const clearDiscount = async () => {
    setPercent(''); setAmount(''); setUntil(''); setReason('');
    if (!t) return;
    setSavingDiscount(true);
    try {
      await setTenantDiscount(t.id, { percent: 0, amount: 0, until: null, reason: null });
      load();
    } catch {
      setDiscountError(tr('tenant.discountClearError', "Could not clear the discount."));
    } finally {
      setSavingDiscount(false);
    }
  };

  const money = (n: number) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: t?.currency ?? 'RON' }).format(n);
  const date = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

  if (error) return <Alert variant="error">{error}</Alert>;
  if (!t) return <div className="flex justify-center p-10"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/tenants" className="text-sm text-subtle-ink hover:text-muted-strong">← {tr('tenant.backToTenants', "Tenants")}</Link>
        <h1 className="text-2xl font-bold text-body">{t.name}</h1>
        {t.isDemo && <span className="rounded bg-subtle px-1.5 py-0.5 text-[10px] text-muted">DEMO</span>}
        {!t.isDemo && (
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant={t.status === 'Suspended' ? 'primary' : 'danger'}
              onClick={toggle}
              loading={busy}
            >
              {t.status === 'Suspended' ? tr('tenant.reactivate', 'Reactivate') : tr('tenant.suspend', 'Suspend')}
            </Button>
            {/* Only after suspension, and only once — offboarding is a decision taken once and
                executed second. The API enforces both; this keeps the button from suggesting
                otherwise. */}
            {t.status === 'Suspended' && !t.dataPurgedAt && (
              <Button variant="danger" onClick={purge} loading={busy}>
                {tr('tenant.purge', 'Delete data…')}
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <div className="mb-2 text-sm font-semibold text-body-strong">{tr('tenant.subscription', "Subscription")}</div>
          <Row label={tr('tenant.plan', "Plan")}>{t.planName}</Row>
          <Row label={tr('tenant.status', "Status")}>{t.status}</Row>
          <Row label={tr('tenant.annualBilling', "Annual billing")}><YesNo v={t.isAnnualBilling} /></Row>
          <Row label={tr('tenant.trialEnds', "Trial ends")}>{date(t.trialEndsAt)}</Row>
          <Row label={tr('tenant.renews', "Renews")}>{t.subscriptionEndsAt ? date(t.subscriptionEndsAt) : '—'}</Row>
          <Row label={tr('tenant.stripeCustomer', "Stripe customer")}><YesNo v={t.hasStripeCustomer} /></Row>
          <Row label={tr('tenant.activeSubscription', "Active subscription")}><YesNo v={t.hasStripeSubscription} /></Row>
          <Row label={tr('tenant.pays', "Pays")}>
            {t.effectiveMonthlyPrice < t.listMonthlyPrice ? (
              <>
                <span className="mr-1.5 font-normal text-subtle-ink line-through">{money(t.listMonthlyPrice)}</span>
                {money(t.effectiveMonthlyPrice)}/mo
              </>
            ) : (
              <>{money(t.listMonthlyPrice)}/mo</>
            )}
          </Row>
        </Card>

        <Card>
          <div className="mb-1 text-sm font-semibold text-body-strong">{tr('tenant.agreedPrice', "Agreed price")}</div>
          <p className="mb-3 text-xs text-muted">
            A discount off their plan, not a fixed price — it keeps tracking the plan, and it reverts
            on its own at the end date. Leave the date empty for as long as they are a customer.
          </p>

          {discountError && <Alert variant="error" className="mb-3">{discountError}</Alert>}

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs uppercase tracking-wide text-subtle-ink">{tr('tenant.percentOff', "Percent off")}</span>
              <input
                type="number" min={0} max={100} step="any" className={field}
                value={percent}
                disabled={Number(amount) > 0}
                onChange={(e) => setPercent(e.target.value)}
                placeholder="e.g. 15"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs uppercase tracking-wide text-subtle-ink">
                or fixed off ({t.currency}/mo)
              </span>
              <input
                type="number" min={0} step="any" className={field}
                value={amount}
                disabled={Number(percent) > 0}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 30"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs uppercase tracking-wide text-subtle-ink">{tr('tenant.until', "Until")}</span>
              <input type="date" className={field} value={until} onChange={(e) => setUntil(e.target.value)} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs uppercase tracking-wide text-subtle-ink">{tr('tenant.reason', "Reason")}</span>
              <input
                type="text" className={field} value={reason} maxLength={300}
                onChange={(e) => setReason(e.target.value)}
                placeholder={tr('tenant.signedAtLaunch', "Signed at launch")}
              />
            </label>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <Button variant="secondary" loading={savingDiscount} onClick={saveDiscount}>{tr('common.save', "Save")}</Button>
            {(t.discountPercent > 0 || t.discountAmount > 0) && (
              <Button variant="ghost" onClick={clearDiscount} disabled={savingDiscount}>{tr('common.remove', "Remove")}</Button>
            )}
            {t.discountUntil && (
              <span className="text-xs text-subtle-ink">Reverts to {money(t.listMonthlyPrice)} on {t.discountUntil}</span>
            )}
          </div>

          <p className="mt-3 text-xs text-amber-600">
            Stripe bills against the price on the subscription. This is applied at checkout for new
            subscriptions; changing it for a clinic already subscribed also needs the change making in
            Stripe until subscription sync is wired up.
          </p>
        </Card>

        <Card>
          <div className="mb-2 text-sm font-semibold text-body-strong">{tr('tenant.platform', "Platform")}</div>
          <Row label={tr('tenant.country', "Country")}>{t.countryCode}</Row>
          <Row label={tr('tenant.walletBalance', "Wallet balance")}>{money(t.walletBalance)}</Row>
          <Row label={tr('tenant.cabinets', "Cabinets")}>{t.cabinets}</Row>
          <Row label={tr('tenant.users', "Users")}>{t.users}</Row>
          <Row label={tr('tenant.patients', "Patients")}>{t.patients}</Row>
          <Row label={tr('tenant.storage', "Storage")}>{t.storageMode} · {(t.storageQuotaMb / 1024).toFixed(0)} GB</Row>
          <Row label={tr('tenant.payouts', "Payouts (Connect)")}><YesNo v={t.connectChargesEnabled} /></Row>
          <Row label={tr('tenant.created', "Created")}>{date(t.createdAt)}</Row>
          {/* Retention is the one process that destroys data on a timer. Shown only once it is in
              motion, so an ordinary clinic's page is not cluttered with empty offboarding rows. */}
          {t.dataPurgedAt
            ? <Row label={tr('tenant.dataPurged', "Data deleted")}>{date(t.dataPurgedAt)}</Row>
            : t.purgeScheduledAt
              ? <Row label={tr('tenant.purgeScheduled', "Deletion scheduled")}>{date(t.purgeScheduledAt)}</Row>
              : null}
        </Card>
      </div>
    </div>
  );
};

export default TenantDetailPage;
